import React, { useState } from "react";
import { ShieldCheck, CheckCircle2 } from "lucide-react";
import api from "../api";

const UPDATE_BTN_STYLE = {
  background: "#0D6EFD",
  color: "#FFFFFF",
  border: "1px solid #0D6EFD",
  fontSize: 11,
  fontWeight: 700,
  padding: "4px 12px",
  borderRadius: 6,
};

const UPLOAD_BTN_STYLE = {
  border: "1px solid #DEE2E6",
  color: "#0D6EFD",
  background: "transparent",
  fontSize: 11,
  fontWeight: 700,
  padding: "8px 10px",
};

/**
 * Shared Aadhaar eKYC + EPFO/UAN block used on Employee and Employer register.
 * After verify: only green "Verified" is shown (no last-4 / UAN). No Update button on register.
 */
export default function IdentityVerificationSection({
  candidateName = "",
  aadhaarVerified,
  epfVerified,
  onAadhaarVerified,
  onEpfVerified,
}) {
  const [identityBusy, setIdentityBusy] = useState(false);
  const [identityError, setIdentityError] = useState("");
  const [identityOk, setIdentityOk] = useState("");
  const [aadhaarFrontName, setAadhaarFrontName] = useState("");
  const [aadhaarBackName, setAadhaarBackName] = useState("");
  const [uanDraft, setUanDraft] = useState("");

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Could not read that file."));
      reader.readAsDataURL(file);
    });

  const handleAadhaarUpload = async (e, side) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (aadhaarVerified) {
      setIdentityError("Aadhaar eKYC is already verified and cannot be changed.");
      e.target.value = "";
      return;
    }
    if (side === "front") setAadhaarFrontName(file.name);
    else setAadhaarBackName(file.name);
    setIdentityError("");
    setIdentityOk("");
    setIdentityBusy(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const contentType = /png/i.test(file.type) ? "PNG" : "JPEG";
      const res = await api.verifyAadhaarQr(dataUrl, contentType, candidateName, { strict: true });
      const fields = res?.data?.fields || res?.fields || {};
      const last4 = String(fields.aadhaar_last4 || "").replace(/\D/g, "").slice(-4);
      if (last4.length !== 4) {
        throw new Error("Aadhaar QR did not return last 4 digits. Upload a clear image of the QR side.");
      }
      const photoUrl = res?.data?.photo?.base64
        ? `data:${res.data.photo.mime_type || "image/jpeg"};base64,${res.data.photo.base64}`
        : "";
      onAadhaarVerified({ ...fields, aadhaar_last4: last4 }, photoUrl);
      setIdentityOk("Aadhaar eKYC verified.");
    } catch (err) {
      setIdentityError(err.message || "Aadhaar verification failed.");
    } finally {
      setIdentityBusy(false);
      e.target.value = "";
    }
  };

  const handleUanVerify = async () => {
    if (epfVerified) {
      setIdentityError("EPFO / UAN is already linked and cannot be changed.");
      return;
    }
    const uan = String(uanDraft || "").replace(/\s+/g, "").toUpperCase();
    if (uan.length < 8) {
      setIdentityError("Enter a valid UAN / EPF number (at least 8 characters).");
      return;
    }
    setIdentityBusy(true);
    setIdentityError("");
    setIdentityOk("");
    try {
      const res = await api.post("/verify/epf", { epfNo: uan });
      onEpfVerified(uan, res);
      setIdentityOk("EPFO / UAN verified.");
    } catch (err) {
      setIdentityError(err.message || "Could not verify EPFO / UAN.");
    } finally {
      setIdentityBusy(false);
    }
  };

  return (
    <>
      <div className="small fw-bold text-uppercase text-primary mb-3">
        1. Identity Verification
      </div>

      <div className="p-3 rounded-3 mb-4 bg-body-tertiary border">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex align-items-center gap-2 small fw-semibold">
            <ShieldCheck size={16} color={aadhaarVerified ? "#16A34A" : "#DC2626"} />
            <span>
              Aadhaar eKYC:{" "}
              <strong style={{ color: aadhaarVerified ? "#16A34A" : "#DC2626" }}>
                {aadhaarVerified ? "Verified" : "Not verified"}
              </strong>
            </span>
          </div>
          <div className="d-flex align-items-center gap-2 small fw-semibold">
            <CheckCircle2 size={16} color={epfVerified ? "#16A34A" : "#DC2626"} />
            <span>
              EPFO / UAN:{" "}
              <strong style={{ color: epfVerified ? "#16A34A" : "#DC2626" }}>
                {epfVerified ? "Verified" : "Not linked"}
              </strong>
            </span>
          </div>
        </div>

        {identityError && (
          <div className="small mt-2 fw-semibold" style={{ color: "#DC2626" }}>{identityError}</div>
        )}
        {identityOk && (
          <div className="small mt-2 fw-semibold" style={{ color: "#16A34A" }}>{identityOk}</div>
        )}
        {identityBusy && (
          <div className="small mt-2 text-secondary">Updating identity details…</div>
        )}

        {!aadhaarVerified && (
          <div className="mt-3 pt-3 border-top">
            <div className="small fw-bold mb-2">Upload Aadhaar front and the QR side to verify eKYC</div>
            <div className="row g-2">
              <div className="col-12 col-md-6">
                <label className="btn btn-sm w-100" style={UPLOAD_BTN_STYLE}>
                  {aadhaarFrontName || "Upload front image"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    hidden
                    disabled={identityBusy}
                    onChange={(e) => handleAadhaarUpload(e, "front")}
                  />
                </label>
              </div>
              <div className="col-12 col-md-6">
                <label className="btn btn-sm w-100" style={UPLOAD_BTN_STYLE}>
                  {aadhaarBackName || "Upload QR / back image"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    hidden
                    disabled={identityBusy}
                    onChange={(e) => handleAadhaarUpload(e, "back")}
                  />
                </label>
              </div>
            </div>
          </div>
        )}

        {!epfVerified && (
          <div className="mt-3 pt-3 border-top">
            <div className="small fw-bold mb-2">Enter UAN / EPF number to link EPFO</div>
            <div className="d-flex flex-wrap gap-2">
              <input
                type="text"
                className="form-control form-control-sm shadow-none"
                style={{ maxWidth: 260 }}
                maxLength={12}
                value={uanDraft}
                onChange={(e) => setUanDraft(e.target.value.replace(/\s+/g, "").replace(/\D/g, "").slice(0, 12))}
                placeholder="12-digit UAN"
                disabled={identityBusy}
              />
              <button
                type="button"
                className="btn btn-sm fw-bold"
                disabled={identityBusy || String(uanDraft || "").length < 8}
                onClick={handleUanVerify}
                style={UPDATE_BTN_STYLE}
              >
                {identityBusy ? "Verifying…" : "Verify"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
